import settings from "../../../constants/settings";
import useFetchAndTrace from "../fetchWithTrace";
import { useState } from "react";
import { ItemType } from "../../../components/internal/parts/InventoryUpdates/MoveContainerAction";

export default function useMoveContainers(
  onSuccess?: () => void,
  // retryAllowingPartNotOnBom re-sends the same move, overriding the check
  // that the part is on the tote's BOM
  onError?: (detail: string, retryAllowingPartNotOnBom?: () => void) => void,
) {
  const fetchAndTrace = useFetchAndTrace();
  const [loading, setLoading] = useState<boolean>(false);

  const makeMoveHandler = (retryAllowingPartNotOnBom: () => void) => {
    return async (response: Response) => {
      setLoading(false);
      if (response.ok) {
        onSuccess?.();
      } else if (response.status === 400 || response.status === 404) {
        const { detail } = (await response.json()) as { detail: string };
        onError?.(detail, retryAllowingPartNotOnBom);
      } else {
        throw Error("Failed to move container or inventory item");
      }
    };
  };

  const errorHandler = () => {
    setLoading(false);
    onError?.("Error moving container or inventory item");
  };

  const moveContainers = (
    relocateBinBarcode: string | null,
    containersToMove: string[],
    itemType: ItemType,
    allowPartNotOnBom: boolean = false,
  ) => {
    setLoading(true);
    void fetchAndTrace(
      `${settings.api_url}/inventory/${itemType}/relocate`,
      makeMoveHandler(() => {
        moveContainers(relocateBinBarcode, containersToMove, itemType, true);
      }),
      errorHandler,
      {
        body: JSON.stringify({
          new_location_bin_barcode: relocateBinBarcode,
          containers: containersToMove,
          allow_part_not_on_bom: allowPartNotOnBom,
        }),
        method: "PUT",
      },
    );
  };

  const moveContainerAndUpdateQty = (
    relocateBinBarcode: string,
    containerQty: number,
    containerBarcode: string,
    allowPartNotOnBom: boolean = false,
  ) => {
    setLoading(true);
    void fetchAndTrace(
      `${settings.api_url}/inventory/containers/move-and-update-qty`,
      makeMoveHandler(() => {
        moveContainerAndUpdateQty(
          relocateBinBarcode,
          containerQty,
          containerBarcode,
          true,
        );
      }),
      errorHandler,
      {
        body: JSON.stringify({
          new_location_bin_barcode: relocateBinBarcode,
          quantity: containerQty,
          flashpcb_container_barcode: containerBarcode,
          allow_part_not_on_bom: allowPartNotOnBom,
        }),
        method: "PUT",
      },
    );
  };

  return {
    moveContainers,
    moveContainerAndUpdateQty,
    movingContainers: loading,
  };
}
